/* =========================================================
   POOLTIME – PRODUKTTILLVAL V6

   Stöd för:
   - Extra skenor via custom.produkttillval
   - Montering via custom.pooltak_montering
   - Checkbox-tillval
   - Radio-tillval
   - Separata Shopify-varianter
   - Bevarar val när Itoris/DPO renderas om
   - Uppdaterar Pooltimes befintliga totalpris
   ========================================================= */

(() => {

  const SECTION_SELECTOR = 'section[id^="MainProduct-"]';

  const ADDON_BLOCK_SELECTOR = '[data-pooltime-addon-block]';
  const INSTALLATION_BLOCK_SELECTOR = '[data-pooltime-installation-block]';

  const INPUT_SELECTOR = 'input[data-pooltime-addon]';

  const selectedBySection = new Map();
  const installationBySection = new Map();

  const mountLocks = new Set();


  /* =========================================================
     HELPERS
     ========================================================= */

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }


  function getSectionId(section) {
    return String(section?.id || '')
      .replace(/^MainProduct-/, '');
  }


  function getSelectedSet(section) {

    const id = getSectionId(section);

    if (!selectedBySection.has(id)) {
      selectedBySection.set(id, new Set());
    }

    return selectedBySection.get(id);
  }


  function getSelectedInstallation(section, groupName) {

    const id = getSectionId(section);

    return installationBySection.get(id + ':' + groupName) || '';
  }


  function setSelectedInstallation(section, variantId, groupName) {

    const id = getSectionId(section);

    installationBySection.set(
      id + ':' + groupName,
      String(variantId || '')
    );
  }


  /* =========================================================
     INPUT TYPE
     ========================================================= */

  function isInstallationInput(input) {

    return input?.classList?.contains(
      'pooltime-product-addon-radio'
    );
  }


  function isCheckboxInput(input) {

    return input?.classList?.contains(
      'pooltime-product-addon-checkbox'
    );
  }


  /* =========================================================
     SELECTED ROW
     ========================================================= */

  function updateRowState(input) {

    if (!input) return;

    const row = input.closest(
      '.pooltime-product-addon-option'
    );

    if (!row) return;

    row.classList.toggle(
      'is-selected',
      input.checked
    );
  }


  function updateAllRowStates(section) {

    section
      .querySelectorAll(INPUT_SELECTOR)
      .forEach(updateRowState);
  }


  /* =========================================================
     SECTION STATE
     ========================================================= */

  function syncSectionState(section) {

    /*
      Endast riktiga Shopify-tillval ska räknas.

      "Ingen montering" har inget data-variant-id
      och ska därför INTE aktivera addon-selected.
    */

    const hasSelectedAddon = Array.from(
      section.querySelectorAll(INPUT_SELECTOR)
    ).some((input) => {

      return (
        input.checked &&
        !input.disabled &&
        input.dataset.variantId
      );

    });


    section.classList.toggle(
      'pooltime-addon-selected',
      hasSelectedAddon
    );
  }


  /* =========================================================
     RESTORE CHECKBOXES
     ========================================================= */

  function restoreCheckboxSelection(section, block) {

    const selected = getSelectedSet(section);

    block
      .querySelectorAll(
        'input.pooltime-product-addon-checkbox[data-pooltime-addon]'
      )
      .forEach((input) => {

        const variantId = String(
          input.dataset.variantId || ''
        );

        input.checked = selected.has(variantId);

        updateRowState(input);

      });
  }


  /* =========================================================
     RESTORE INSTALLATION RADIO
     ========================================================= */

  function restoreInstallationSelection(section, block) {
    const radios = Array.from(block.querySelectorAll('input.pooltime-product-addon-radio[data-pooltime-addon]'));
    const groups = new Set(radios.map(input => input.name));
    groups.forEach(name => {
      const group = radios.filter(input => input.name === name);
      const selected = getSelectedInstallation(section, name);
      const match = group.find(input => !input.disabled && input.dataset.variantId === selected);
      const fallback = group.find(input => !input.disabled && !input.dataset.variantId);
      const active = match || fallback;
      if (!match) setSelectedInstallation(section, '', name);
      group.forEach(input => { input.checked = input === active; updateRowState(input); });
    });
  }

  /* =========================================================
     RESTORE ALL
     ========================================================= */

  function restoreSelection(section, root) {

    restoreCheckboxSelection(
      section,
      root
    );

    restoreInstallationSelection(
      section,
      root
    );

    updateAllRowStates(section);

    syncSectionState(section);
  }


  /* =========================================================
     DPO HELPERS
     ========================================================= */

  function fieldTitle(field) {

    if (!field) return '';

    const title =
      field.querySelector(
        ':scope > label:not(.admin__field-option)'
      ) ||
      field.querySelector(
        ':scope > legend'
      ) ||
      field.querySelector(
        ':scope > .admin__field-label'
      ) ||
      field.querySelector(
        ':scope > label'
      );

    return normalizeText(
      title?.textContent || ''
    );
  }


  function getDpoFields(dpo) {

    if (!dpo) return [];

    return Array.from(
      dpo.querySelectorAll(
        '.field.xbg:not(.choice), ' +
        '.field.admin__field:not(.choice), ' +
        '.fieldset, ' +
        '.admin__fieldset'
      )
    ).filter((field) => {

      return (
        !field.closest(ADDON_BLOCK_SELECTOR) &&
        !field.closest(INSTALLATION_BLOCK_SELECTOR) &&
        !field.closest('[data-pooltime-crane-block]')
      );

    });
  }


  function findOldExtraRailsField(dpo) {

    return getDpoFields(dpo).find(
      (field) => {

        const title =
          fieldTitle(field);

        return (
          title.includes('extra skenor') ||
          title.includes('extra rails')
        );

      }
    );
  }


  function findAssemblyField(dpo) {

    return getDpoFields(dpo).find(
      (field) => {

        const title =
          fieldTitle(field);

        return (
          title.includes('önskas ihopsättning') ||
          title.includes('ihopsättning') ||
          title.includes('assembly') ||
          title.includes('montering av pooltak')
        );

      }
    );
  }


  /* =========================================================
     HIDE OLD DPO FIELDS
     ========================================================= */

  function hideOldExtraRailsDpo(dpo) {

    const oldField =
      findOldExtraRailsField(dpo);

    if (oldField) {

      oldField.dataset.pooltimeOldExtraRails =
        'true';

      oldField.style.setProperty(
        'display',
        'none',
        'important'
      );
    }

    return oldField;
  }


  function hideOldAssemblyDpo(dpo) {

    const oldField =
      findAssemblyField(dpo);

    if (oldField) {

      oldField.dataset.pooltimeOldAssembly =
        'true';

      oldField.style.setProperty(
        'display',
        'none',
        'important'
      );
    }

    return oldField;
  }


  /* =========================================================
     CLONE TEMPLATE
     ========================================================= */

  function cloneMetafieldBlock(section) {

    const sectionId =
      getSectionId(section);

    const template =
      section.querySelector(
        '#PooltimeProductAddonsTemplate-' +
        CSS.escape(sectionId)
      );


    if (
      !template?.content?.firstElementChild
    ) {
      return null;
    }


    return template.content
      .firstElementChild
      .cloneNode(true);
  }


  /* =========================================================
     HIDE DUPLICATE COMPLEMENTARY PRODUCTS
     ========================================================= */

  function hideDuplicateComplementary(
    section,
    block
  ) {

    const handles = Array.from(
      block.querySelectorAll(
        '[data-addon-product-handle]'
      )
    )
      .map(
        (el) =>
          el.dataset.addonProductHandle
      )
      .filter(Boolean);


    if (!handles.length) return;


    document
      .querySelectorAll(
        'product-recommendations.complementary-products, ' +
        '.complementary-products'
      )
      .forEach((root) => {

        const containsSameAddon =
          handles.some((handle) => {

            return root.querySelector(
              `a[href*="/products/${CSS.escape(handle)}"]`
            );

          });


        if (containsSameAddon) {

          root.style.setProperty(
            'display',
            'none',
            'important'
          );

          root.dataset.pooltimeMovedToAddon =
            'true';
        }

      });
  }


  /* =========================================================
     PLACE BLOCK
     ========================================================= */

  function placeBlock(
    section,
    dpo,
    block
  ) {

    const app =
      dpo?.closest('.shopify-app-block') ||
      section.querySelector(
        '[id*="__dpo_dynamic_product_options_"]'
      );


    const anchor =
      app ||
      dpo?.closest('.dpo-container') ||
      dpo;


    if (
      anchor &&
      anchor.parentNode
    ) {

      if (
        block.nextElementSibling !== anchor
      ) {

        anchor.before(block);
      }

    } else {

      const variants =
        section.querySelector(
          'variant-selects, variant-radios'
        );


      const buttons =
        section.querySelector(
          '.product-form__quantity, product-form'
        );


      if (variants) {

        if (
          variants.nextElementSibling !== block
        ) {

          variants.after(block);
        }

      } else if (buttons) {

        if (
          block.nextElementSibling !== buttons
        ) {

          buttons.before(block);
        }

      } else {

        return;
      }
    }


    if (dpo) {

      /*
        Dölj gamla DPO-fält först när
        våra nya metafältsbaserade alternativ
        verkligen finns.
      */

      if (
        block.querySelector(
          ADDON_BLOCK_SELECTOR
        )
      ) {
        hideOldExtraRailsDpo(dpo);
      }


      if (
        block.querySelector(
          INSTALLATION_BLOCK_SELECTOR
        )
      ) {
        hideOldAssemblyDpo(dpo);
      }
    }


    restoreSelection(
      section,
      block
    );


    hideDuplicateComplementary(
      section,
      block
    );
  }


  /* =========================================================
     MOUNT
     ========================================================= */

  function mountAddonBlock(section) {

    const sectionId =
      getSectionId(section);


    if (
      mountLocks.has(sectionId)
    ) {
      return;
    }


    const template =
      section.querySelector(
        '#PooltimeProductAddonsTemplate-' +
        CSS.escape(sectionId)
      );


    /*
      Inget metafält =
      gör ingenting.
    */

    if (
      !template?.content?.firstElementChild
    ) {
      return;
    }


    const dpo =
      section.querySelector(
        '#itoris_dynamicproductoptions'
      ) ||
      section.querySelector(
        '.itoris_dynamicproductoptions'
      );


    /*
      V6 wrapper.
    */

    const existing =
      section.querySelector(
        `[data-pooltime-addons-wrapper][data-section-id="${CSS.escape(sectionId)}"]`
      );


    if (existing) {

      placeBlock(
        section,
        dpo,
        existing
      );

      return;
    }


    mountLocks.add(sectionId);


    try {

      const alreadyMounted =
        section.querySelector(
          `[data-pooltime-addons-wrapper][data-section-id="${CSS.escape(sectionId)}"]`
        );


      if (alreadyMounted) {

        placeBlock(
          section,
          dpo,
          alreadyMounted
        );

        return;
      }


      const block =
        cloneMetafieldBlock(section);


      if (!block) return;


      placeBlock(
        section,
        dpo,
        block
      );

    } finally {

      mountLocks.delete(sectionId);
    }
  }


  /* =========================================================
     SCHEDULE MOUNT
     ========================================================= */

  function scheduleMount(
    section,
    delay = 20
  ) {

    clearTimeout(
      section._pooltimeAddonMountTimer
    );


    section._pooltimeAddonMountTimer =
      setTimeout(() => {

        mountAddonBlock(section);

      }, delay);
  }


  /* =========================================================
     INIT SECTION
     ========================================================= */

  function initSection(section) {

    if (
      !section ||
      section.dataset.pooltimeAddonsV6 ===
        'true'
    ) {
      return;
    }


    section.dataset.pooltimeAddonsV6 =
      'true';


    /*
      Försök direkt.
    */

    mountAddonBlock(section);


    /*
      Itoris kan renderas efteråt.
    */

    const observer =
      new MutationObserver(() => {

        scheduleMount(
          section,
          10
        );

      });


    observer.observe(
      section,
      {
        childList: true,
        subtree: true
      }
    );


    /*
      Korta retries.
    */

    [
      50,
      100,
      200,
      400,
      800,
      1500,
      2500
    ].forEach((delay) => {

      setTimeout(
        () => mountAddonBlock(section),
        delay
      );

    });
  }


  function initAll() {

    document
      .querySelectorAll(
        SECTION_SELECTOR
      )
      .forEach(initSection);
  }


  /* =========================================================
     CHANGE EVENT
     ========================================================= */

  document.addEventListener(
    'change',
    (event) => {

      const input =
        event.target.closest(
          INPUT_SELECTOR
        );


      if (!input) return;


      const section =
        input.closest(
          SECTION_SELECTOR
        );


      if (!section) return;


      const variantId =
        String(
          input.dataset.variantId || ''
        );


      /*
        =====================================================
        RADIO – MONTERING
        =====================================================
      */

      if (
        isInstallationInput(input)
      ) {

        if (!input.checked) return;


        /*
          Spara valt monteringsalternativ.
          Tom variantId = Ingen montering.
        */

        setSelectedInstallation(
          section,
          variantId,
          input.name
        );


        /*
          Uppdatera ALLA radio-rader
          i samma grupp.
        */

        const radioName =
          input.getAttribute('name');


        section
          .querySelectorAll(
            `input.pooltime-product-addon-radio[name="${CSS.escape(radioName)}"]`
          )
          .forEach((radio) => {

            updateRowState(radio);

          });


        syncSectionState(section);


        /*
          Meddela befintlig prislogik.
        */

        section.dispatchEvent(
          new CustomEvent(
            'pooltime:addon-change',
            {
              bubbles: true,

              detail: {
                type: 'installation',
                variantId:
                  variantId || null,
                checked: true,
                priceCents:
                  Number(
                    input.dataset.priceCents ||
                    0
                  )
              }
            }
          )
        );


        return;
      }


      /*
        =====================================================
        CHECKBOX – EXTRA SKENOR
        =====================================================
      */

      if (
        isCheckboxInput(input)
      ) {

        const selected =
          getSelectedSet(section);


        if (
          input.checked &&
          variantId
        ) {

          selected.add(
            variantId
          );

        } else {

          selected.delete(
            variantId
          );
        }


        updateRowState(input);

        syncSectionState(section);


        section.dispatchEvent(
          new CustomEvent(
            'pooltime:addon-change',
            {
              bubbles: true,

              detail: {
                type: 'addon',
                variantId:
                  variantId || null,
                checked:
                  input.checked,
                priceCents:
                  Number(
                    input.dataset.priceCents ||
                    0
                  )
              }
            }
          )
        );
      }

    }
  );


  /* =========================================================
     INFO BUTTON
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {

      const button =
        event.target.closest(
          '[data-pooltime-addon-info-button]'
        );


      if (!button) return;


      event.preventDefault();
      event.stopPropagation();


      /*
        Viktigt:
        fungerar både för Extra skenor
        och Montering.
      */

      const block =
        button.closest(
          `${ADDON_BLOCK_SELECTOR}, ${INSTALLATION_BLOCK_SELECTOR}, [data-pooltime-crane-block]`
        );


      const info =
        block?.querySelector(
          '[data-pooltime-addon-info]'
        );


      if (!info) return;


      const open =
        info.hasAttribute(
          'hidden'
        );


      info.toggleAttribute(
        'hidden',
        !open
      );


      button.setAttribute(
        'aria-expanded',
        open ? 'true' : 'false'
      );

    }
  );


  /* =========================================================
     SHOPIFY SECTION RELOAD
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      const section =
        event.target.matches?.(
          SECTION_SELECTOR
        )
          ? event.target
          : event.target.querySelector?.(
              SECTION_SELECTOR
            );


      if (!section) return;


      section.dataset.pooltimeAddonsV6 =
        'false';


      mountLocks.delete(
        getSectionId(section)
      );


      initSection(section);

    }
  );


  /* =========================================================
     START
     ========================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );

  } else {

    initAll();

  }

})();


